import { forwardRef, useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Label } from "components/custom/programs/fields";
import { Button } from "components/core/basic/Button";
import { useFieldArray, useWatch } from "react-hook-form";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { GrDrag, GrFormClose } from "react-icons/gr";
import RuleForm from "./RuleForm";
import HeaderFields from "./HeaderFields";
import { RULE_DEFAULTS } from "./constants";
import { useProgramContext } from "./ProgramContext";
import ActionForm from "./ActionForm";
import ToolTips from "utils/toolTips";
import { LuCopyPlus } from "react-icons/lu";
import ConfirmationModal from "components/core/blocks/ConfirmationModal";
import HarvestingRuleForm from "./HarvestingRuleForm";

const RULE_COMPONENTS = {
  harvesting: HarvestingRuleForm,
  default: RuleForm,
};

const RulesContainer = forwardRef(({ children }, ref) => {
  return (
    <ul ref={ref} className="rules" style={{ paddingLeft: 0, listStyle: "none" }}>
      {children}
    </ul>
  );
});

const Rule = forwardRef(
  (
    { control, formState, setValue, date, id, dragHandleProps, snapshot, index, remove, insert, fieldName, ...props },
    ref,
  ) => {
    const { engine } = useProgramContext();
    const [expanded, setExpanded] = useState(true);
    const [height, setHeight] = useState(0);
    const contentRef = useRef(null);
    useEffect(() => {
      if (!contentRef.current) return; // wait for the elementRef to be available
      const resizeObserver = new ResizeObserver(() => {
        setHeight(contentRef.current.clientHeight + 20);
      });
      resizeObserver.observe(contentRef.current);
      return () => resizeObserver.disconnect(); // clean up
    }, [expanded]);

    const ruleSpec = useWatch({
      control,
      name: `${fieldName}.${index}`,
    });

    const RuleComponent = RULE_COMPONENTS[engine] || RULE_COMPONENTS.default;

    return (
      <li ref={ref} {...props} className={"rule card p-4 mb-4" + (snapshot.isDragging ? "hovering" : "")}>
        <Row>
          <Col xs={9} className="d-flex align-items-center">
            <div {...dragHandleProps}>
              <span
                style={{
                  fontSize: "14px",
                  marginRight: "1rem",
                  color: "#666",
                }}
              >
                <GrDrag />
              </span>
            </div>
            <span
              style={{
                cursor: "pointer",
                color: "#623F7F",
                fontSize: "14px",
                fontWeight: "600",
                width: "60px",
              }}
              onClick={() => setExpanded(!expanded)}
            >
              Rule {index + 1}
            </span>
            <span style={{ fontSize: "12px", color: "#666" }}>{ruleSpec?.comment}</span>
          </Col>
          <Col xs={3} className="d-flex align-items-center justify-content-end">
            <span
              style={{
                cursor: "pointer",
                fontSize: "18px",
                color: "#666",
              }}
              onClick={() => {
                insert(index + 1, { ...ruleSpec, comment: (ruleSpec?.comment ?? "") + " (copy)" });
              }}
            >
              <LuCopyPlus />
            </span>
            <span
              style={{
                cursor: "pointer",
                fontSize: "24px",
                color: "#666",
              }}
              onClick={() => remove(index)}
            >
              <ConfirmationModal
                title="Delete Rule"
                confirmationMessage="Are you sure you want to delete this rule?"
                onConfirm={() => remove(index)}
                inlineModal
                modalButtonText={<GrFormClose />}
                confirmButtonText="Delete"
              />
            </span>
          </Col>
        </Row>
        <Row
          style={{
            maxHeight: expanded ? height : "0",
            transition: "all 0.2s ease-in-out",
            overflow: expanded ? "unset" : "hidden",
            animation: "0.2s delay-overflow",
          }}
        >
          <Col xs={12}>
            <div ref={contentRef}>
              <Row className="mt-4 fs-standard">
                <Col xs={12}>
                  <RuleComponent
                    control={control}
                    formState={formState}
                    fieldName={`${fieldName}.${index}`}
                    setValue={setValue}
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </li>
    );
  },
);

const ProgramsForm = ({ control, formState, setValue, hideHeader, fieldName }) => {
  const fieldNamespace = (name) => (fieldName ? `${fieldName}.${name}` : name);
  const { fields, append, remove, move, insert } = useFieldArray({
    control,
    name: fieldNamespace("rules"),
  });

  const { engine } = useProgramContext();

  return (
    <Row>
      {!hideHeader && <HeaderFields control={control} formState={formState} fieldName={fieldName} />}
      <Col xs={12} className="mt-4">
        {engine === "bidding" && (
          <Row className="mb-3">
            <Col xs={12} className="fs-standard py-3">
              <strong>New Keyword/Target Action:</strong>{" "}
              <ToolTips
                toolTip={"Perform this action when a new keyword or target is added to the media plan."}
                position="top"
                id={"initialActionTooltip"}
              />
            </Col>

            <ActionForm
              fieldName={fieldNamespace("initial_action")}
              control={control}
              formState={formState}
              initialAction={true}
            />
          </Row>
        )}
        {engine === "lineitem_bidding" && (
          <Row className="mb-3">
            <Col xs={12} className="fs-standard py-3">
              <strong>New Lineitem Actions:</strong>{" "}
              <ToolTips
                toolTip={"Perform this action when a new line item is added to the Order."}
                position="top"
                id={"initialActionTooltip"}
              />
            </Col>

            <ActionForm
              fieldName={fieldNamespace(`initial_action`)}
              control={control}
              formState={formState}
              initialAction={true}
            />
          </Row>
        )}
        <Row>
          <Col xs={12}>
            <Label>Rules</Label>
          </Col>
        </Row>
        <DragDropContext
          onDragEnd={({ source, destination }) => {
            move(source.index, destination.index);
          }}
        >
          <Droppable droppableId="rules-wrapper">
            {(provided) => (
              <RulesContainer ref={provided.innerRef} {...provided.droppableProps}>
                {fields.map((field, index) => {
                  return (
                    <Draggable draggableId={`rule-${field.id}`} index={index} key={field.id}>
                      {(_provided, _snapshot) => (
                        <Rule
                          ref={_provided.innerRef}
                          fieldName={fieldNamespace("rules")}
                          dragHandleProps={_provided.dragHandleProps}
                          {..._provided.draggableProps}
                          snapshot={_snapshot}
                          index={index}
                          remove={remove}
                          insert={insert}
                          control={control}
                          formState={formState}
                          setValue={setValue}
                          {...field}
                        />
                      )}
                    </Draggable>
                  );
                })}
              </RulesContainer>
            )}
          </Droppable>
        </DragDropContext>
        <Row>
          <Col xs={12} className="d-flex justify-content-end">
            {formState.errors?.[fieldName]?.rules && (
              <span className="text-danger">{formState.errors?.[fieldName]?.rules?.root?.message}</span>
            )}
            {formState.errors?.rules && <span className="text-danger">{formState.errors?.rules?.root?.message}</span>}
            <Button
              onPress={() => {
                append(RULE_DEFAULTS[engine]);
              }}
            >
              Add Rule
            </Button>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default ProgramsForm;
